import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

import { RdsCodeSortDirection, RdsCodeSortType, RdsTabulationSortOptions } from '../tabulation-table/index';

@Component({
  selector: 'rds-tabulation-sort-options',
  template: `
    <div fxLayout="column">
      <h4 class="mat-caption">SORT ROWS BY</h4>
      <div class="sort-option-group" fxLayout="row" fxLayoutAlign="start center">
        <button
          mat-stroked-button
          [disabled]="disabled"
          (click)="handleRowTypeChange()"
          matTooltip="Toggle through name, value, natural"
          type="button"
        >
          {{ value.rowType }}
        </button>
        <button
          (click)="handleRowDirChange()"
          [disabled]="disabled"
          fxLayout="row"
          fxLayoutAlign="center center"
          mat-icon-button
          [matTooltip]="'Switch to ' + (value.rowDirection === 'ASC' ? 'descending' : 'ascending')"
          matTooltipPosition="after"
          type="button"
        >
          <mat-icon>{{ value.rowDirection === 'ASC' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
        </button>
      </div>

      <h4 class="mat-caption">SORT COLUMNS BY</h4>
      <div class="sort-option-group" fxLayout="row" fxLayoutAlign="start center">
        <button
          mat-stroked-button
          [disabled]="disabled"
          (click)="handleColTypeChange()"
          matTooltip="Toggle through name, value, natural"
          type="button"
        >
          {{ value.colType }}
        </button>
        <button
          (click)="handleColDirChange()"
          [disabled]="disabled"
          fxLayout="row"
          fxLayoutAlign="center center"
          mat-icon-button
          [matTooltip]="'Switch to ' + (value.colDirection === 'ASC' ? 'descending' : 'ascending')"
          matTooltipPosition="after"
          type="button"
        >
          <mat-icon>{{ value.colDirection === 'ASC' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      rds-tabulation-sort-options .mat-caption {
        text-transform: uppercase;
        font-weight: 500;
        margin-top: 0px;
        margin-bottom: 8px;
      }

      rds-tabulation-sort-options .sort-option-group {
        margin-bottom: 16px;
      }

      rds-tabulation-sort-options .sort-option-group .mat-stroked-button {
        margin-right: 4px;
        min-width: 95px;
      }

      rds-tabulation-sort-options .sort-option-group .mat-icon-button {
        height: 36px;
        width: 36px;
        line-height: 34px;
        border-style: solid;
        border-width: 1px;
        border-radius: 4px;
      }

      rds-tabulation-sort-options .sort-option-group .mat-icon-button .mat-button-wrapper .mat-icon {
        height: 18px;
        width: 18px;
        line-height: 18px;
        font-size: 18px;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RdsTabulationSortOptionsComponent implements ControlValueAccessor {
  @Input()
  get value(): RdsTabulationSortOptions {
    return this._value;
  }
  set value(value: RdsTabulationSortOptions) {
    if (value !== this._value) {
      this._value = value || new RdsTabulationSortOptions();
      this.changeDetectorRef.markForCheck();
    }
  }
  private _value = new RdsTabulationSortOptions();

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this._disabled) {
      this._disabled = newValue;
      this.changeDetectorRef.markForCheck();
    }
  }
  private _disabled = false;

  @Output() readonly change = new EventEmitter<RdsTabulationSortOptions>();

  _onTouched: () => any = () => {};
  private _controlValueAccessorChangeFn: (value: any) => void = () => {};

  constructor(
    elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl,
    private changeDetectorRef: ChangeDetectorRef,
    private _focusMonitor: FocusMonitor
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this._focusMonitor.monitor(elementRef, true).subscribe((focusOrigin) => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => this._onTouched());
      }
    });
  }

  handleRowTypeChange() {
    const rowType: RdsCodeSortType = this.getNewSortType(this.value.rowType);
    this.value = { ...this.value, rowType };
    this._emitChangeEvent();
  }

  handleRowDirChange() {
    const rowDirection: RdsCodeSortDirection = this.value.rowDirection === 'DESC' ? 'ASC' : 'DESC';
    this.value = { ...this.value, rowDirection };
    this._emitChangeEvent();
  }

  handleColTypeChange() {
    const colType: RdsCodeSortType = this.getNewSortType(this.value.colType);
    this.value = { ...this.value, colType };
    this._emitChangeEvent();
  }

  handleColDirChange() {
    const colDirection: RdsCodeSortDirection = this.value.colDirection === 'DESC' ? 'ASC' : 'DESC';
    this.value = { ...this.value, colDirection };
    this._emitChangeEvent();
  }

  registerOnChange(fn: any): void {
    this._controlValueAccessorChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  _emitChangeEvent() {
    this._controlValueAccessorChangeFn(this.value);
    this.change.emit(this.value);
  }

  private getNewSortType(value: RdsCodeSortType): RdsCodeSortType {
    switch (value) {
      case 'NAME':
        return 'VALUE';
      case 'VALUE':
        return 'NATURAL';
      default:
        return 'NAME';
    }
  }
}
